import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { SatellitePharmacyServiceService } from 'src/app/services/satellite-pharmacy-service.service';

enum SpsStatus {
  /** 閒置狀態：目前沒有傳輸作業，或 trySend 連線測試成功後回到此狀態。 */
  Idle = 0,
  /** 嘗試連線中：手動/初始化執行 trySend 連線測試期間使用。 */
  TryingConnect = 8,
  /** 處理中狀態：呼叫 send 或 trySend 開始執行時進入。 */
  Processing = 1,
  /** 完成狀態：send 最終成功時使用，10 秒後會自動回到 Idle。 */
  Complete = 2,
  /** 失敗狀態：send 最終失敗時使用，10 秒後會自動回到 Idle。 */
  Fail = 3,
  /** 連線錯誤狀態：trySend 測試失敗或發生例外時使用。 */
  Error = 9
}

@Component({
  selector: 'app-sps-status',
  templateUrl: './sps-status.component.html',
  styleUrls: ['./sps-status.component.css']
})
export class SpsStatusComponent implements OnInit, OnDestroy {
  constructor(private readonly satelliteService: SatellitePharmacyServiceService) { }

  status: SpsStatus = SpsStatus.Idle;
  isTrySending = false;
  showPopup = false;
  popupMessage = '';
  private statusSub?: Subscription;
  private resetIdleTimer?: ReturnType<typeof setTimeout>;
  private popupTimer?: ReturnType<typeof setTimeout>;
  private readonly trySendFailedMessage = '無法與衛星藥局建立連線';
  private readonly trySendSuccessMessage = '與衛星藥局連線成功!';
  private readonly popupSuccessAutoHideMs = 3000;
  private readonly popupFailedAutoHideMs = 5000;
  private currentPatientNo = '';

  // tooltip 改用原生 title，避免額外依賴 MatTooltip module。
  private readonly statusMeta: Record<SpsStatus, { icon: string; cssClass: string }> = {
    [SpsStatus.Idle]: { icon: 'pause_circle', cssClass: 'status-idle' },
    [SpsStatus.TryingConnect]: { icon: 'wifi_tethering', cssClass: 'status-trying-connect' },
    [SpsStatus.Processing]: { icon: 'sync', cssClass: 'status-processing' },
    [SpsStatus.Complete]: { icon: 'check_circle', cssClass: 'status-complete' },
    [SpsStatus.Fail]: { icon: 'cancel', cssClass: 'status-fail' },
    [SpsStatus.Error]: { icon: 'error', cssClass: 'status-error' }
  };

  ngOnInit(): void {
    this.statusSub = this.satelliteService.transferEvents$.subscribe(evt => {
      if (evt.type === 'trySendStarted') {
        this.setStatus(SpsStatus.TryingConnect);
        this.showPopupMessage('嘗試連線中...');
        return;
      }
      if (evt.type === 'trySendFinished') {
        if (evt.success) {
          this.setStatus(SpsStatus.Idle);
          this.showPopupMessage(this.trySendSuccessMessage);
          this.startHidePopupTimer(this.popupSuccessAutoHideMs);
        } else {
          this.handleTrySendFailed();
        }
        return;
      }
      if (evt.type === 'trySendFailed') {
        return;
      }
      if (evt.type === 'sendStarted') {
        this.clearResetIdleTimer();
        this.status = SpsStatus.Processing;
        this.currentPatientNo = evt.patientNo ?? '';
        return;
      }
      if (evt.type === 'sendFinished') {
        this.status = evt.success ? SpsStatus.Complete : SpsStatus.Fail;
        this.currentPatientNo = evt.patientNo ?? this.currentPatientNo;
        if (evt.success) {
          this.showPopupMessage(`傳送完成 ${this.currentPatientNo || ''}`);
          this.startHidePopupTimer(this.popupSuccessAutoHideMs);
        }
        this.startResetIdleTimer();
      }
    });
    this.initializeStatusFromLastTrySendResult();
  }

  ngOnDestroy(): void {
    this.statusSub?.unsubscribe();
    this.clearResetIdleTimer();
    this.clearPopupTimer();
  }

  get statusIcon(): string {
    return this.statusMeta[this.status].icon;
  }

  get statusClass(): string {
    return this.statusMeta[this.status].cssClass;
  }

  get statusTip(): string {
    if (this.status === SpsStatus.Idle) {
      return `衛星藥局:${this.satelliteService.Host || ''}`;
    }
    if (this.status === SpsStatus.Processing) {
      return `正在傳送 ${this.currentPatientNo || ''}`;
    }
    if (this.status === SpsStatus.Complete) {
      return `傳送完成 ${this.currentPatientNo || ''}`;
    }
    if (this.status === SpsStatus.Fail) {
      return `傳送失敗 ${this.currentPatientNo || ''}`;
    }
    if (this.status === SpsStatus.Error) {
      return `無法建立連線(${this.satelliteService.Host })，點擊已重新測試`;
    }
    return '嘗試與衛星藥局建立連線中';
  }

  setStatus(status: SpsStatus): void {
    this.clearResetIdleTimer();
    this.status = status;
    if (status === SpsStatus.Complete || status === SpsStatus.Fail) {
      this.startResetIdleTimer();
    }
  }

  setTransferResult(success: boolean): void {
    if (this.status !== SpsStatus.Processing) {
      return;
    }

    this.status = success ? SpsStatus.Complete : SpsStatus.Fail;
    this.startResetIdleTimer();
  }

  async onTrySendClick(): Promise<void> {
    await this.runTrySend();
  }

  private initializeStatusFromLastTrySendResult(): void {
    if (this.satelliteService.lastTrySendSuccess === false) {
      this.handleTrySendFailed();
      return;
    }
    if (this.satelliteService.lastTrySendSuccess === true) {
      this.setStatus(SpsStatus.Idle);
    }
  }

  private async runTrySend(): Promise<void> {
    if (this.isTrySending) {
      return;
    }
    this.isTrySending = true;
    try {
      await this.satelliteService.trySend('', false);
    } catch {
      // 狀態與訊息交由 SatellitePharmacyServiceService 事件統一驅動。
    } finally {
      this.isTrySending = false;
    }
  }

  private handleTrySendFailed(): void {
    this.setStatus(SpsStatus.Error);
    this.showPopupMessage(this.trySendFailedMessage);
    this.startHidePopupTimer(this.popupFailedAutoHideMs);
  }

  private startResetIdleTimer(): void {
    this.clearResetIdleTimer();
    this.resetIdleTimer = setTimeout(() => {
      if (this.status === SpsStatus.Complete || this.status === SpsStatus.Fail) {
        this.status = SpsStatus.Idle;
      }
    }, 10000);
  }

  private clearResetIdleTimer(): void {
    if (!this.resetIdleTimer) {
      return;
    }
    clearTimeout(this.resetIdleTimer);
    this.resetIdleTimer = undefined;
  }

  private showPopupMessage(message: string): void {
    this.clearPopupTimer();
    this.popupMessage = message;
    this.showPopup = true;
  }

  private hidePopup(): void {
    this.clearPopupTimer();
    this.showPopup = false;
    this.popupMessage = '';
  }

  private startHidePopupTimer(ms: number): void {
    this.clearPopupTimer();
    this.popupTimer = setTimeout(() => this.hidePopup(), ms);
  }

  private clearPopupTimer(): void {
    if (!this.popupTimer) {
      return;
    }
    clearTimeout(this.popupTimer);
    this.popupTimer = undefined;
  }
}
